import React, { useState, useEffect } from 'react'
import './DataTable.css'
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react'

const DataTable = ({ data, onSort, sortConfig, onCompanyClick }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1)
  }, [data])

  const formatMarketCap = (value) => {
    if (!value) return 'N/A'
    if (value >= 1000000000000) {
      return `$${(value / 1000000000000).toFixed(1)}T`
    } else if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`
    }
    return `$${value.toLocaleString()}`
  }

  const formatPrice = (value) => {
    if (!value) return 'N/A'
    return `$${parseFloat(value).toFixed(2)}`
  }

  const formatPercentage = (value) => {
    if (!value) return 'N/A'
    return `${parseFloat(value).toFixed(2)}%`
  }

  const formatNumber = (value) => {
    if (!value) return 'N/A'
    return parseFloat(value).toFixed(2)
  }

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="sort-icon" />
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="sort-icon" /> : 
      <ArrowDown className="sort-icon" />
  }

  const handleSort = (key) => {
    onSort(key)
  }

  const handleRowClick = (company) => {
    if (onCompanyClick) {
      onCompanyClick(company)
    }
  }

  const totalPages = Math.ceil(data.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentData = data.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }
    
    return pages
  }

  return (
    <div className="data-table-container">
      <div className="table-header">
        <h3>TSX Companies ({data.length.toLocaleString()} results)</h3>
        <div className="table-info">
          Showing {startIndex + 1}-{Math.min(endIndex, data.length)} of {data.length.toLocaleString()} companies
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('Ticker')}>
                <div className="th-content">
                  Ticker
                  {getSortIcon('Ticker')}
                </div>
              </th>
              <th onClick={() => handleSort('Company')}>
                <div className="th-content">
                  Company
                  {getSortIcon('Company')}
                </div>
              </th>
              <th onClick={() => handleSort('Price Today')}>
                <div className="th-content">
                  Price
                  {getSortIcon('Price Today')}
                </div>
              </th>
              <th onClick={() => handleSort('Market Cap')}>
                <div className="th-content">
                  Market Cap
                  {getSortIcon('Market Cap')}
                </div>
              </th>
              <th onClick={() => handleSort('P/E')}>
                <div className="th-content">
                  P/E
                  {getSortIcon('P/E')}
                </div>
              </th>
              <th onClick={() => handleSort('Dividend Yield')}>
                <div className="th-content">
                  Div Yield
                  {getSortIcon('Dividend Yield')}
                </div>
              </th>
              <th onClick={() => handleSort('ROE')}>
                <div className="th-content">
                  ROE
                  {getSortIcon('ROE')}
                </div>
              </th>
              <th onClick={() => handleSort('Sector')}>
                <div className="th-content">
                  Sector
                  {getSortIcon('Sector')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, index) => (
              <tr 
                key={`${row.Ticker}-${index}`} 
                className="table-row clickable"
                onClick={() => handleRowClick(row)}
              >
                <td className="ticker-cell">
                  <div className="ticker-content">
                    <span className="ticker-symbol">{row.Ticker}</span>
                    <a 
                      href={`https://www.tmxmoney.com/en/quote/${row.Ticker}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="link-icon" />
                    </a>
                  </div>
                </td>
                <td className="company-cell">
                  <div className="company-info">
                    <span className="company-name">{row.Company}</span>
                    {row.Industry && (
                      <span className="company-industry">{row.Industry}</span>
                    )}
                  </div>
                </td>
                <td className="price-cell">
                  <span className="price-value">{formatPrice(row['Price Today'])}</span>
                  {row['Price 1y ago'] && row['Price Today'] && (
                    <span className={`price-change ${parseFloat(row['Price Today']) > parseFloat(row['Price 1y ago']) ? 'positive' : 'negative'}`}>
                      {((parseFloat(row['Price Today']) - parseFloat(row['Price 1y ago'])) / parseFloat(row['Price 1y ago']) * 100).toFixed(1)}%
                    </span>
                  )}
                </td>
                <td className="market-cap-cell">
                  {formatMarketCap(row['Market Cap'])}
                </td>
                <td className="pe-cell">
                  {formatNumber(row['P/E'])}
                </td>
                <td className="dividend-cell">
                  {formatPercentage(row['Dividend Yield'])}
                </td>
                <td className="roe-cell">
                  {formatPercentage(row['ROE'])}
                </td>
                <td className="sector-cell">
                  <span className="sector-badge">{row.Sector}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                className={`page-btn ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                onClick={() => typeof page === 'number' && handlePageChange(page)}
                disabled={page === '...'}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

export default DataTable 